import React, { ReactNode } from "react";

import List from "veneer/List";
import VeneerListItem from "veneer/ListItem";
import Stack from "veneer/Stack";
import styled from "veneer/styled";
import { Colors, useTheme } from "veneer/Theme";
import Typography from "veneer/Typography";

import { Device } from "cortex/ui/src/fleet/view/device/model";

export interface UpgradeDrawerProps {
  onCancel: () => void;
  open: boolean;
  selectedItems: ReadonlyArray<Device>;
  setSelectedItems: (items: ReadonlyArray<Device>) => void;
}

export interface UpgradeRow {
  device: Device;
  isValid: boolean;
  reason?: string;
}

export interface UpgradeEligibility {
  isValid: boolean;
  reason?: string;
}

interface BaseDrawerProps {
  children: ReactNode;
  drawerHeader?: ReactNode;
  title?: ReactNode;
}

export const ListWrapper = styled(List)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  "& .MuiSvgIcon-root": {
    marginRight: theme.spacing(2),
  },
}));

export const ErrorListItem = styled(VeneerListItem)(({ theme }) => ({
  backgroundColor: Colors.red100,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const ListItem = styled(VeneerListItem)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const DrawerHeader = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  borderBottomLeftRadius: theme.shape.borderRadius * 4,
  borderBottomRightRadius: theme.shape.borderRadius * 4,
  boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.12)",
  padding: theme.spacing(3, 4),
}));

export default function BaseDrawer({
  children,
  drawerHeader,
  title,
}: BaseDrawerProps) {
  const appTheme = useTheme();
  const hasDrawerHeader = !!drawerHeader || !!title;

  return (
    <Stack bgcolor={appTheme.palette.background.body} height="100%">
      {hasDrawerHeader && (
        <DrawerHeader spacing={3}>
          {title && (
            <Typography color={Colors.gray1000} variant="h5">
              {title}
            </Typography>
          )}
          {drawerHeader}
        </DrawerHeader>
      )}
      <Stack bgcolor={appTheme.palette.background.body} flex={1} p={1}>
        {children}
      </Stack>
    </Stack>
  );
}
